import React, { useState, useEffect } from 'react';
import { 
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Chip,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Divider,
  Sheet,
  Accordion,
  AccordionGroup,
  AccordionSummary,
  AccordionDetails,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  useTheme,
} from '@mui/joy';
import { 
  FaCalendarAlt, 
  FaEnvelope, 
  FaEdit, 
  FaPlus,
  FaHistory,
  FaClock,
  FaUser
} from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { directSupabase } from '../lib/supabaseClient';
import { sendTemplateEmail, getGraphClient, processTemplateVariables, cleanupRemainingTemplateVariables, outlookCalendarItemUrl } from '../utils/graphService';
import { createSurveyCalendarEventWithId, updateSurveyCalendarEventWithId } from '../utils/graphService';
import { clearAllCaches } from '../hooks/useOutlookCalendar';
import { emailTemplates, type EmailTemplateType } from '../lib/emailTemplateService';
import type { CalendarEventSettings } from '../lib/emailTemplateService';
import type { Customer } from '../services/customerService';
import type { Project } from '../types/project';
import { emailActivity, type EmailActivityLog } from '../lib/emailActivityService';
import { useCustomerContext } from '../contexts/CustomerContext';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { TimePicker15Min } from './TimePicker15Min';
import { documentsIntegrationService } from '../modules/docs/services/documents-integration.service';
import { isEventTypeAllowed } from '../constants/organizations';
import { normalizeInvoiceRole, preferredEventInvoice } from '../modules/docs/utils/invoiceLedger';

// Types
export type EventType = EmailTemplateType;

export interface ProjectEvent {
  id: string;
  project_id: string;
  event_type: EventType;
  event_date: string | null;
  end_date?: string | null;
  metadata?: Record<string, unknown> | null;
  calendar_event_id: string | null;
  email_sent_id: string | null;
  email_template_used: string | null;
  email_sent_at: string | null;
  email_status: string | null;
  created_at: string | null;
  updated_at: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  is_automated?: boolean | null;
  notes?: string | null;
  title?: string | null;
  reference_subtask_key?: string | null;
  status?: string | null;
}

interface EventManagerProps {
  projectId: string;
  customer?: Customer;
  compact?: boolean;
  modalOnly?: boolean;
  onEventsChange?: () => void;
  openCreateKey?: number;
  preferredEventType?: EventType | null;
}

const CALENDAR_EVENT_TYPES = new Set([
  'firstSurveyAppointmentConfirmation',
  'finalSurveyAppointmentConfirmation',
  'installationDateConfirmation',
  'deliveryDateConfirmation',
]);

const INSTALL_DURATION_MINUTES = 420;

// Event configurations
const EVENT_CONFIGS: Partial<Record<EmailTemplateType, { 
  name: string; 
  requiresCalendarEvent: boolean; 
  referenceTask?: string;
}>> = {
  enduranceDoors: {
    name: 'Endurance Doors',
    requiresCalendarEvent: false
  },
  roofReplacement: {
    name: 'Roof Replacement',
    requiresCalendarEvent: false
  },
  keystoneIntroduction: {
    name: 'Keystone Introduction',
    requiresCalendarEvent: false
  },
  firstSurveyAppointmentRequest: {
    name: 'First Survey Appointment Request',
    requiresCalendarEvent: false,
    referenceTask: 'firstSurveyAppointmentRequest'
  },
  firstSurveyAppointmentConfirmation: {
    name: 'First Survey Appointment Confirmation',
    requiresCalendarEvent: true,
    referenceTask: 'firstSurveyAppointmentConfirmation'
  },
  finalSurveyAppointmentRequest: {
    name: 'Final Survey Appointment Request',
    requiresCalendarEvent: false,
    referenceTask: 'finalSurveyAppointmentRequest'
  },
  finalSurveyAppointmentConfirmation: {
    name: 'Final Survey Appointment Confirmation',
    requiresCalendarEvent: true,
    referenceTask: 'finalSurveyAppointmentConfirmation'
  },
  installationDateConfirmation: {
    name: 'Installation Date Confirmation',
    requiresCalendarEvent: true,
    referenceTask: 'installationDateConfirmation'
  },
  deliveryDateConfirmation: {
    name: 'Delivery Date Confirmation',
    requiresCalendarEvent: true,
    referenceTask: 'deliveryDateConfirmation'
  },
  outstandingPayment: {
    name: 'Outstanding Payment',
    requiresCalendarEvent: false,
    referenceTask: 'outstandingPayment'
  },
  depositRequest: {
    name: 'Deposit Request',
    requiresCalendarEvent: false,
    referenceTask: 'depositRequest'
  },
  otherKeystoneServices: {
    name: 'Other Keystone Services',
    requiresCalendarEvent: false
  },
  completionEmail: {
    name: 'Completion Email',
    requiresCalendarEvent: false
  },
  requestReview: {
    name: 'Request Review',
    requiresCalendarEvent: false,
    referenceTask: 'Request Review'
  },
  newsletter: {
    name: 'Newsletter',
    requiresCalendarEvent: false
  }
};

// Add type for event insert response
type EventInsertResponse = {
  id: string;
  project_id: string;
  event_type: EventType;
  event_date: string | null;
  email_template_used: string | null;
  email_sent_id: string | null;
  email_status: string | null;
  email_sent_at: string | null;
  calendar_event_id: string | null;
  created_at: string;
  updated_at: string;
}[];

// Add type for audit log
interface EventAuditLog {
  id: string;
  event_id: string;
  changed_at: string | null;
  changed_by: string | null;
  change_type: 'INSERT' | 'UPDATE' | 'DELETE';
  old_values?: any;
  new_values?: any;
  metadata?: any;
}

// Add a new component for the email activity viewer
const EmailActivityViewer: React.FC<{ event: ProjectEvent }> = ({ event }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <IconButton 
        size="sm" 
        variant="plain" 
        color="neutral"
        onClick={() => setIsOpen(true)}
        title="View Email Activity"
      >
        <IconWrapper icon={FaEnvelope} />
      </IconButton>
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Email Activity</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography level="body-sm">
                <strong>Event Type:</strong> {EVENT_CONFIGS[event.event_type]?.name}
              </Typography>
              <Typography level="body-sm">
                <strong>Created:</strong> {event.created_at ? new Date(event.created_at).toLocaleString() : 'Unknown'}
              </Typography>
              <Typography level="body-sm">
                <strong>Template Used:</strong> {event.email_template_used}
              </Typography>
              {/* Add more email activity details here */}
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
    </>
  );
};

// Utility to extract all {{variable}} placeholders from a string
function extractTemplateVariables(template: string | null): string[] {
  if (!template) return [];
  const regex = /{{(\w+)}}/g;
  const vars = new Set<string>();
  let match;
  while ((match = regex.exec(template)) !== null) {
    vars.add(match[1]);
  }
  return Array.from(vars);
}

// Utility to build a variables object from project, customer, and event context
function buildTemplateVariables(templates: (string | null)[], context: Record<string, any>): Record<string, string> {
  const allVars = new Set<string>();
  templates.forEach(tpl => extractTemplateVariables(tpl).forEach(v => allVars.add(v)));
  const result: Record<string, string> = {};
  allVars.forEach(v => {
    if (context[v] !== undefined && context[v] !== null) {
      result[v] = String(context[v]);
    }
  });
  return result;
}

// Add a helper function to build template variables based on event type
function buildEventTemplateVariables(eventType: EventType, eventDate: string, eventTime: string, project: any, customerData: any) {
  const baseVariables = {
    projectName: project.project_name || '',
    customerName: customerData.name || '',
    customerEmail: customerData.email || '',
    customerAddress: customerData.address || '',
    projectDetails: project.notes || '',
    customerNotes: project.notes || '',
    customerPhone: customerData.phone || '',
    ...project,
    ...customerData
  };

  if (eventType === 'installationDateConfirmation') {
    return {
      ...baseVariables,
      installationDate: eventDate,
      installationTime: eventTime,
      installationDateLong: eventDate && eventTime ? new Date(`${eventDate}T${eventTime}`).toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }) : ''
    };
  } else {
    return {
      ...baseVariables,
      surveyDate: eventDate,
      surveyTime: eventTime,
      surveyDateLong: eventDate && eventTime ? new Date(`${eventDate}T${eventTime}`).toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }) : ''
    };
  }
}

// Compatibility function to convert unified Customer to old format
function convertToLegacyCustomer(customer: Customer): any {
  // Debug logging
  console.log('Converting customer to legacy format:', {
    id: customer.id,
    name: customer.name,
    company: customer.company,
    organization_name: customer.company || ''
  });
  
  return {
    id: customer.id,
    name: customer.name,
    first_name: customer.name.split(' ')[0] || '',
    last_name: customer.name.split(' ').slice(1).join(' ') || '',
    phone_number: customer.phone,
    email: customer.email,
    address: customer.address,
    billing_address: null, // Not available in unified type
    organization_id: null, // Not available in unified type
    organization_name: customer.company || '', // Map company to organization_name for graphService
    created_at: customer.created_at,
    updated_at: customer.updated_at
  };
}

const EventManager: React.FC<EventManagerProps> = ({
  projectId,
  customer,
  compact = false,
  modalOnly = false,
  onEventsChange,
  openCreateKey = 0,
  preferredEventType = null,
}) => {
  const theme = useTheme();
  const { user } = useSupabaseAuth();
  const [events, setEvents] = useState<ProjectEvent[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<EventType | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<Set<string>>(new Set());
  const [eventDate, setEventDate] = useState<string>('');
  const [eventTime, setEventTime] = useState<string>('');
  const [isMultiDay, setIsMultiDay] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<string>('');
  const [eventDuration, setEventDuration] = useState<number>(60);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailLog, setEmailLog] = useState<EmailActivityLog | null>(null);
  const [emailModalError, setEmailModalError] = useState<string | null>(null);
  const [emailModalLoading, setEmailModalLoading] = useState(false);
  const [templateSettings, setTemplateSettings] = useState<CalendarEventSettings | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateEvent, setDuplicateEvent] = useState<ProjectEvent | null>(null);
  const [pendingEventData, setPendingEventData] = useState<any>(null);
  const [auditLogs, setAuditLogs] = useState<Record<string, EventAuditLog[]>>({});
  const [isOperationInProgress, setIsOperationInProgress] = useState(false);
  // Deposit request state - JARVIS only
  const [depositInvoice, setDepositInvoice] = useState<any | null>(null);
  const [jarvisDocuments, setJarvisDocuments] = useState<any[]>([]);
  const [depositWarnings, setDepositWarnings] = useState<string[]>([]);
  const [depositBlockingError, setDepositBlockingError] = useState<string | null>(null);
  // Template/preview state
  const [templateHtml, setTemplateHtml] = useState<string>('');
  const [templateSubject, setTemplateSubject] = useState<string>('');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewSubject, setPreviewSubject] = useState<string>('');

  useEffect(() => {
    if (!openCreateKey) return;
    setSelectedEventType(preferredEventType);
    setIsModalOpen(true);
  }, [openCreateKey, preferredEventType]);

  // Fetch template settings when event type changes
  useEffect(() => {
    if (selectedEventType) {
      const organizationName = customer?.company || null;
      if (!organizationName) {
        setError('Customer organization is required to load templates. Please ensure the customer has an organization set.');
        setTemplateHtml('');
        setTemplateSubject('');
        setTemplateSettings(null);
        return;
      }
      emailTemplates.getByType(selectedEventType, organizationName).then(({ data }) => {
        const tpl = (data as any) || null;
        if (!data) {
          setError(`No template found for event type "${EVENT_CONFIGS[selectedEventType]?.name}" for organization "${organizationName}". Please create a template in the Admin Panel.`);
          setTemplateHtml('');
          setTemplateSubject('');
          setTemplateSettings(null);
          return;
        }
        setError(null);
        setTemplateHtml(tpl?.template || '');
        setTemplateSubject(tpl?.subject || '');
        // Cast the Json type to CalendarEventSettings
        const settings = (data as any)?.calendar_event_settings as CalendarEventSettings | null;
        setTemplateSettings(settings);
        // Installs default to a 7-hour day; other events take the template's default if available
        if (selectedEventType === 'installationDateConfirmation') {
          setEventDuration(INSTALL_DURATION_MINUTES);
        } else if (settings?.duration_minutes) {
          setEventDuration(settings.duration_minutes);
        }
      }).catch(error => {
        console.warn('Failed to fetch template settings:', error);
        setError(`Failed to load template: ${error.message}`);
        setTemplateSettings(null);
      });
      // Duplicate check on event type selection
      const existing = events.find(e => e.event_type === selectedEventType);
      if (existing) {
        let existingDate = '';
        let existingTime = '';
        if (existing.event_date) {
          const d = new Date(existing.event_date);
          existingDate = d.toISOString().slice(0, 10);
          // Use local time for the input fields
          existingTime = d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
        }
        setEventDate(existingDate);
        setEventTime(existingTime);
        setDuplicateEvent(existing);
        setPendingEventData({
          eventDate: existingDate,
          eventTime: existingTime,
          eventDuration,
          selectedEventType
        });
      } else {
        setDuplicateEvent(null);
        setPendingEventData(null);
        setEventDate('');
        setEventTime('');
      }
      // Deposit/outstanding validation and invoice fetch (retry briefly to allow link writes to propagate)
      (async () => {
        if ((selectedEventType === 'depositRequest' || selectedEventType === 'outstandingPayment') && projectId) {
          try {
            setDepositBlockingError(null);
            setDepositWarnings([]);
            setDepositInvoice(null);
            setJarvisDocuments([]);
            
            // Fetch JARVIS documents only
            const jarvisDocs = await documentsIntegrationService.getDocumentsForProjectEvents(projectId);
            setJarvisDocuments(jarvisDocs);
            
            const relevantJarvisDocs = selectedEventType === 'depositRequest'
              ? jarvisDocs.filter((doc) => normalizeInvoiceRole(doc.invoiceRole) === 'deposit' || (doc.kind === 'Invoice' && doc.hasDeposit))
              : jarvisDocs.filter((doc) => {
                const role = normalizeInvoiceRole(doc.invoiceRole);
                return role === 'works' || role === 'variation' || (doc.kind === 'Invoice' && !role);
              });
            
            if (relevantJarvisDocs.length === 0) {
              if (selectedEventType === 'depositRequest') {
                setDepositBlockingError('No deposit invoice is linked to this workspace. Accept First Survey with a deposit, or link the deposit invoice first.');
              } else {
                setDepositBlockingError('No balance invoice is linked to this workspace. Issue the balance invoice on the Invoice tab first.');
              }
              return;
            }
            const latestJarvisDoc = preferredEventInvoice(
              relevantJarvisDocs.map((doc) => ({ ...doc, role: doc.invoiceRole })),
              selectedEventType,
            ) || relevantJarvisDocs[0];
            
            // Calculate outstanding amount using the service
            const outstandingAmount = documentsIntegrationService.calculateOutstandingBalance(latestJarvisDoc);
            
            // Create a mock invoice structure for JARVIS documents
            const mockInvoice = {
              id: latestJarvisDoc.id,
              document_ref: latestJarvisDoc.invoiceRef || latestJarvisDoc.lifecycleRef,
              title: latestJarvisDoc.title,
              grand_total: latestJarvisDoc.grandTotal,
              deposit_amount: latestJarvisDoc.depositAmount,
              deposit_percentage: latestJarvisDoc.depositPercentage ? latestJarvisDoc.depositPercentage * 100 : null,
              has_deposit: latestJarvisDoc.hasDeposit,
              amount_paid: 0,
              amount_outstanding: outstandingAmount,
              payment_status: 'unpaid',
              customer_address: customer?.address || '',
              payment_link: '',
              payment_url: '',
              deposits: latestJarvisDoc.hasDeposit ? [{
                id: 1,
                quote_id: parseInt(latestJarvisDoc.id),
                deposit_amount: latestJarvisDoc.depositAmount,
                deposit_percentage: latestJarvisDoc.depositPercentage ? latestJarvisDoc.depositPercentage * 100 : 0,
                description: 'JARVIS Document Deposit'
              }] : []
            };
            setDepositInvoice(mockInvoice as any);
            
            // Check deposit for JARVIS documents
            const hasDeposit = Boolean(mockInvoice.has_deposit) || (mockInvoice.deposits && mockInvoice.deposits.length > 0) || (mockInvoice.deposit_amount && mockInvoice.deposit_amount > 0);
            const warnings: string[] = [];
            if (!hasDeposit) {
              if (selectedEventType === 'depositRequest') {
                setDepositBlockingError('This document has no deposit configured. Please add a deposit to the quote/estimate before sending a deposit request.');
              } else {
                warnings.push('This document has no deposit configured. The Outstanding Payment email will request the full outstanding balance.');
              }
            }
            setDepositWarnings(warnings);
          } catch (e:any) {
            setDepositBlockingError(e?.message || 'Unable to validate invoice for deposit request.');
          }
        } else {
          setDepositBlockingError(null);
          setDepositWarnings([]);
          setDepositInvoice(null);
        }
      })();
    } else {
      setTemplateSettings(null);
      setDuplicateEvent(null);
      setPendingEventData(null);
      setEventDate('');
      setEventTime('');
    }
  }, [selectedEventType, events]);

  // Compute preview whenever relevant inputs change
  useEffect(() => {
    const computePreview = () => {
      if (!selectedEventType || !templateHtml) {
        setPreviewHtml('');
        setPreviewSubject('');
        return;
      }
      // Build variables similar to send path
      // Base variables common to all templates
      const baseVars: Record<string, any> = {
        projectName: (selectedEventType === 'depositRequest' || selectedEventType === 'outstandingPayment') && depositInvoice
          ? (depositInvoice as any).title || project?.project_name || ''
          : project?.project_name || '',
        projectDetails: (project as any)?.notes || '',
        customerName: customer?.name || '',
        customerEmail: customer?.email || '',
        customerPhone: (customer as any)?.phone || (customer as any)?.phone_number || '',
        customerCompany: (customer as any)?.company || (customer as any)?.organization_name || '',
        customerAddress: customer?.address || '',
        customerNotes: (project as any)?.notes || '',
        companyName: (customer as any)?.company || (customer as any)?.organization_name || ''
      };

      let vars: Record<string, any> = {};
      const evDate = eventDate;
      const evTime = eventTime;
      if (selectedEventType === 'installationDateConfirmation') {
        vars = {
          installationDate: evDate,
          installationTime: evTime,
          installationDateLong: evDate && evTime ? new Date(`${evDate}T${evTime}`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''
        };
      } else if (selectedEventType === 'deliveryDateConfirmation') {
        vars = {
          deliveryDate: evDate,
          deliveryTime: evTime,
          deliveryDateLong: evDate && evTime ? new Date(`${evDate}T${evTime}`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''
        };
      } else if ((selectedEventType === 'depositRequest' || selectedEventType === 'outstandingPayment') && depositInvoice) {
        const total = Number((depositInvoice as any).grand_total ?? 0) || 0;
        const paidDirect = Number((depositInvoice as any).amount_paid ?? 0) || 0;
        const paidFromHistory = Array.isArray((depositInvoice as any).payment_history)
          ? (depositInvoice as any).payment_history.reduce((sum: number, p: any) => sum + (Number(p.payment_amount) || 0), 0)
          : 0;
        const paid = Math.max(paidDirect, paidFromHistory);
        const outstanding = (depositInvoice as any).amount_outstanding != null
          ? Number((depositInvoice as any).amount_outstanding)
          : Math.max(total - paid, 0);
        const rawPct = Number((depositInvoice as any).deposit_percentage);
        const pct = Number.isFinite(rawPct) ? rawPct : null;
        let deposit = Number((depositInvoice as any).deposit_amount ?? 0) || 0;
        
        // Only calculate deposit if the invoice actually has a deposit configured
        const hasDeposit = (depositInvoice as any).has_deposit;
        if (hasDeposit) {
          if (!deposit) {
            const listDeposit = depositInvoice.deposits?.[0]?.deposit_amount;
            if (typeof listDeposit === 'number') deposit = listDeposit;
          }
          if (!deposit && pct != null) {
            deposit = Number(((total * pct) / 100).toFixed(2));
          }
        } else {
          // If invoice has no deposit configured, ensure deposit is 0
          deposit = 0;
        }
        const paymentLink = (depositInvoice as any).payment_link || (depositInvoice as any).payment_url || '';
        const invoiceAddress = (depositInvoice as any).customer_address || (depositInvoice as any).billing_address || (depositInvoice as any).customer_billing_address || ((depositInvoice as any).customer_info?.[0]?.address) || '';
        // Compute outstanding explicitly: total minus deposit minus any additional payments beyond the deposit
        const nonDepositPaid = Math.max(paid - deposit, 0);
        const balance = Math.max(total - deposit - nonDepositPaid, 0);
        // For both deposit requests and outstanding payments, show remaining after deposit
        const amountOutstandingForTemplate = balance;
        const resolvedDepositPct =
          (hasDeposit && pct != null)
            ? pct
            : (hasDeposit && total > 0 && deposit > 0)
              ? (deposit / total) * 100
              : null;
        vars = {
          invoiceRef: depositInvoice.document_ref || String(depositInvoice.id || ''),
          invoiceStatus: (depositInvoice as any).payment_status || (depositInvoice as any).status || '',
          depositAmount: deposit.toFixed(2),
          depositPercentage: resolvedDepositPct != null ? String(Math.round(resolvedDepositPct)) : '',
          amountOutstanding: amountOutstandingForTemplate.toFixed(2),
          paymentDueDate: (depositInvoice as any).payment_due_date || (depositInvoice as any).due_date || '',
          paymentLink,
          customerAddress: customer?.address || invoiceAddress,
          depositPresent: deposit > 0 ? 'true' : ''
        };
      } else {
        vars = {
          surveyDate: evDate,
          surveyTime: evTime,
          surveyDateLong: evDate && evTime ? new Date(`${evDate}T${evTime}`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''
        };
      }

      const merged = { ...baseVars, ...vars };
      let html = processTemplateVariables(templateHtml, merged);
      let subject = processTemplateVariables(templateSubject, merged);
      html = cleanupRemainingTemplateVariables(html);
      subject = cleanupRemainingTemplateVariables(subject);
      setPreviewHtml(html);
      setPreviewSubject(subject);
    };
    computePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventType, templateHtml, templateSubject, eventDate, eventTime, depositInvoice, customer?.address]);

  // Fetch project data and update customer fields if needed
  const fetchProject = async () => {
    try {
      const { data, error } = await directSupabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();
      
      if (error) {
        console.error('Error fetching project:', error);
        setProject(null);
      } else {
        setProject(data);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      setProject(null);
    }
  };

  const fetchAuditLogs = async (events: ProjectEvent[]) => {
    try {
      const logsByEvent: Record<string, EventAuditLog[]> = {};
      for (const event of events) {
        const { data, error, status } = await directSupabase
          .from('project_event_history')
          .select('*')
          .eq('event_id', event.id)
          .order('changed_at', { ascending: false });

        if (error) {
          console.error(`Error fetching audit logs for event ${event.id}:`, error);
          continue;
        }

        logsByEvent[event.id] = (data || []).map((log: any) => ({
          id: log.id,
          event_id: log.event_id,
          changed_at: log.changed_at,
          changed_by: log.changed_by,
          change_type: log.change_type,
          old_values: log.old_values,
          new_values: log.new_values,
          metadata: log.metadata
        }));
      }
      setAuditLogs(logsByEvent);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const { data, error } = await directSupabase
        .from('project_events')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      } else {
        setEvents(data || []);
        onEventsChange?.();
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  // Load available templates for the customer's organization
  useEffect(() => {
    const loadAvailableTemplates = async () => {
      const organizationName = customer?.company || null;
      if (!organizationName) {
        setAvailableTemplates(new Set());
        return;
      }
      
      try {
        const { data, error } = await emailTemplates.getAll(organizationName);
        if (error) {
          console.error('Error loading templates:', error);
          setAvailableTemplates(new Set());
          return;
        }
        
        // Create a set of template types that exist for this organization
        const templateTypes = new Set((data || []).map((t: any) => t.template_type));
        setAvailableTemplates(templateTypes);
      } catch (error) {
        console.error('Error loading templates:', error);
        setAvailableTemplates(new Set());
      }
    };
    
    loadAvailableTemplates();
  }, [customer?.company]);

  // Initial fetch and refresh on projectId change
  useEffect(() => {
    fetchProject();
    fetchEvents();
  }, [projectId]);

  // After events are loaded or changed, fetch audit logs for each event
  useEffect(() => {
    if (events && events.length > 0) {
      fetchAuditLogs(events);
    } else {
      setAuditLogs({});
    }
  }, [events]);

  // Add a new function to wait for event to be available
  const waitForEvent = async (eventId: string, maxAttempts = 5): Promise<boolean> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const { data, error } = await directSupabase
          .from('project_events')
          .select('id')
          .eq('id', eventId)
          .single();

        if (error) {
          console.error('Error checking event:', error);
          return false;
        }

        if (data) {
          return true;
        }
      } catch (error) {
        console.error('Error checking event:', error);
      }

      // Wait before next attempt
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    return false;
  };

  // Add a function to verify project exists
  const verifyProject = async (projectId: string): Promise<boolean> => {
    try {
      const { data, error } = await directSupabase
        .from('projects')
        .select('id')
        .eq('id', projectId)
        .single();
      
      if (error) {
        console.error('Error verifying project:', error);
        return false;
      }
      
      return !!data;
    } catch (error) {
      console.error('Error verifying project:', error);
      return false;
    }
  };

  // Helper to check for duplicate event
  const checkForDuplicateEvent = (eventType: EventType) => {
    return events.find(e => e.event_type === eventType);
  };

  // Helper to determine if a date is in British Summer Time (BST)
  function isBST(dateStr: string) {
    const d = new Date(dateStr + 'T00:00:00Z');
    const year = d.getUTCFullYear();
    // Last Sunday in March
    const start = new Date(Date.UTC(year, 2, 31));
    start.setUTCDate(31 - start.getUTCDay());
    // Last Sunday in October
    const end = new Date(Date.UTC(year, 9, 31));
    end.setUTCDate(31 - end.getUTCDay());
    return d >= start && d < end;
  }

  // Remove the duplicate modal and unify submit logic
  const handleEventSubmit = async () => {
    if (!selectedEventType || !project) return;
    // keep modal open so validation/errors are visible
    setIsSubmitting(true);
    setIsOperationInProgress(true);
    setError(null);
    try {
      if (!customer) {
        throw new Error("Customer data not available. Please try again.");
      }
      const customerName = customer.name || '';
      const customerEmail = customer.email || '';
      const customerAddress = customer.address || '';
      if (!customerEmail) {
        setError("Customer email not found");
        setIsSubmitting(false);
        return;
      }
      const customerData: Customer = {
        id: customer.id,
        name: customerName,
        email: customerEmail || null,
        phone: customer.phone || '',
        address: customerAddress,
        company: customer.company || undefined,
        created_at: customer.created_at,
        updated_at: customer.updated_at,
        source: customer.source || 'api'
      };

      if (selectedEventType === 'depositRequest' || selectedEventType === 'outstandingPayment') {
        if (depositBlockingError) {
          setError(depositBlockingError);
          setIsSubmitting(false);
          setIsOperationInProgress(false);
          return;
        }
        // Accept JARVIS invoice only
        if (!depositInvoice) {
          setError('Select an invoice for this project before sending this request.');
          setIsSubmitting(false);
          setIsOperationInProgress(false);
          return;
        }
      }

      // Handle updating an existing event
      if (duplicateEvent) {
        const eventConfig = EVENT_CONFIGS[selectedEventType];
        if (!eventConfig) {
          setError('Invalid event type selected.');
          setIsSubmitting(false);
          setIsOperationInProgress(false);
          return;
        }
        const updateOffset = (eventDate && isBST(eventDate)) ? '+01:00' : '+00:00';
        
        // Send email for the update
        const { emailLogId, status } = await sendTemplateEmail({
          templateType: selectedEventType,
          project,
          customer: convertToLegacyCustomer(customerData),
          variables: selectedEventType === 'installationDateConfirmation' ? {
            installationDate: eventDate,
            installationTime: eventTime,
            installationDateLong: eventDate && eventTime ? new Date(`${eventDate}T${eventTime}`).toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }) : ''
          } : selectedEventType === 'deliveryDateConfirmation' ? {
            deliveryDate: eventDate,
            deliveryTime: eventTime,
            deliveryDateLong: eventDate && eventTime ? new Date(`${eventDate}T${eventTime}`).toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }) : ''
          } : (selectedEventType === 'depositRequest' || selectedEventType === 'outstandingPayment') && depositInvoice ? (() => {
            const total = Number((depositInvoice as any).grand_total ?? 0) || 0;
            const paidDirect = Number((depositInvoice as any).amount_paid ?? 0) || 0;
            const paidFromHistory = Array.isArray((depositInvoice as any).payment_history)
              ? (depositInvoice as any).payment_history.reduce((sum: number, p: any) => sum + (Number(p.payment_amount) || 0), 0)
              : 0;
            const paid = Math.max(paidDirect, paidFromHistory);
            const outstanding = (depositInvoice as any).amount_outstanding != null
              ? Number((depositInvoice as any).amount_outstanding)
              : Math.max(total - paid, 0);
            const rawPct = Number((depositInvoice as any).deposit_percentage);
            const pct = Number.isFinite(rawPct) ? rawPct : null;
            let deposit = Number((depositInvoice as any).deposit_amount ?? 0) || 0;
            
            // Only calculate deposit if the invoice actually has a deposit configured
            const hasDeposit = (depositInvoice as any).has_deposit;
            if (hasDeposit) {
              if (!deposit) {
                const listDeposit = depositInvoice.deposits?.[0]?.deposit_amount;
                if (typeof listDeposit === 'number') deposit = listDeposit;
              }
              if (!deposit && pct != null) {
                deposit = Number(((total * pct) / 100).toFixed(2));
              }
            } else {
              // If invoice has no deposit configured, ensure deposit is 0
              deposit = 0;
            }
            const paymentLink = (depositInvoice as any).payment_link || (depositInvoice as any).payment_url || '';
            const invoiceAddress = (depositInvoice as any).customer_address || (depositInvoice as any).billing_address || (depositInvoice as any).customer_billing_address || ((depositInvoice as any).customer_info?.[0]?.address) || '';
            // Compute outstanding explicitly: total minus deposit minus any additional payments beyond the deposit
            const nonDepositPaid = Math.max(paid - deposit, 0);
            const balance = Math.max(total - deposit - nonDepositPaid, 0);
            const resolvedDepositPct =
              (hasDeposit && pct != null)
                ? pct
                : (hasDeposit && total > 0 && deposit > 0)
                  ? (deposit / total) * 100
                  : null;
            return {
              invoiceRef: depositInvoice.document_ref || String(depositInvoice.id || ''),
              invoiceStatus: (depositInvoice as any).payment_status || (depositInvoice as any).status || '',
              depositAmount: deposit.toFixed(2),
              depositPercentage: resolvedDepositPct != null ? String(Math.round(resolvedDepositPct)) : '',
              amountOutstanding: balance.toFixed(2),
              paymentDueDate: (depositInvoice as any).payment_due_date || (depositInvoice as any).due_date || '',
              paymentLink,
              customerAddress: customerData.address || invoiceAddress,
              depositPresent: deposit > 0 ? 'true' : '',
              projectName: (depositInvoice as any).title || project.project_name || '',
              documentId: String((depositInvoice as any).id || ''),
              sentVersionNumber: (depositInvoice as any).sentVersionNumber != null ? String((depositInvoice as any).sentVersionNumber) : '',
            };
          })() : {
            surveyDate: eventDate,
            surveyTime: eventTime,
            surveyDateLong: eventDate && eventTime ? new Date(`${eventDate}T${eventTime}`).toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }) : ''
          }
        });
        
        // Prepare update data
        const updateData: any = {
          updated_at: new Date().toISOString(),
          updated_by: user?.id || null,
          email_sent_id: emailLogId,
          email_status: status,
          email_sent_at: new Date().toISOString()
        };

        // Only update date/time for calendar events
        if (eventConfig && eventConfig.requiresCalendarEvent) {
          updateData.event_date = (eventDate && eventTime) ? `${eventDate}T${eventTime}:00${updateOffset}` : null;
        }

        // Update calendar event if needed
        let calendarEventIdToUpdate: string | null = null;
        if (eventConfig && eventConfig.requiresCalendarEvent && eventDate && eventTime) {
          const calendarEventId = duplicateEvent.calendar_event_id;
          
          // Transform calendar settings to match expected type
          const transformedSettings = templateSettings ? {
            subject: templateSettings.subject || undefined,
            body: templateSettings.body || undefined,
            location: templateSettings.location || undefined,
            duration_minutes: eventDuration,
            attendees: templateSettings.attendees?.map(attendee => ({
              email: attendee.emailAddress.address,
              name: attendee.emailAddress.name,
              type: attendee.type || undefined
            }))
          } : undefined;

          if (calendarEventId) {
            try {
              await updateSurveyCalendarEventWithId({
                eventId: calendarEventId,
                project,
                date: eventDate,
                time: eventTime,
                calendarEventSettings: {
                  ...(transformedSettings && typeof transformedSettings === 'object' ? transformedSettings : {}),
                  duration_minutes: eventDuration
                },
                variables: buildEventTemplateVariables(selectedEventType, eventDate, eventTime, project, customerData)
              });
              calendarEventIdToUpdate = calendarEventId;
            } catch (err: any) {
              if (err?.message && err.message.includes('The specified object was not found in the store')) {
                // Calendar event was deleted, create a new one
                calendarEventIdToUpdate = await createSurveyCalendarEventWithId({
                  project,
                  date: eventDate,
                  time: eventTime,
                  calendarEventSettings: {
                    ...(transformedSettings && typeof transformedSettings === 'object' ? transformedSettings : {}),
                    duration_minutes: eventDuration
                  },
                  variables: buildEventTemplateVariables(selectedEventType, eventDate, eventTime, project, customerData)
                });
              } else {
                throw err;
              }
            }
          } else {
            // Create new calendar event
            calendarEventIdToUpdate = await createSurveyCalendarEventWithId({
              project,
              date: eventDate,
              time: eventTime,
              calendarEventSettings: {
                ...(transformedSettings && typeof transformedSettings === 'object' ? transformedSettings : {}),
                duration_minutes: eventDuration
              },
              variables: buildEventTemplateVariables(selectedEventType, eventDate, eventTime, project, customerData)
            });
          }
          updateData.calendar_event_id = calendarEventIdToUpdate;
        }

        // Update the existing event using direct Supabase
        try {
          const oldValuesSnapshot = duplicateEvent;
          const { data, error } = await directSupabase
            .from('project_events')
            .update(updateData)
            .eq('id', duplicateEvent.id)
            .select()
            .single();
          
          if (error) {
            setError('Failed to update existing event: ' + error.message);
          } else {
            // Insert audit trail entry capturing old/new values
            try {
              await directSupabase.from('project_event_history').insert({
                event_id: duplicateEvent.id,
                change_type: 'UPDATE',
                changed_at: new Date().toISOString(),
                changed_by: user?.id || null,
                old_values: oldValuesSnapshot,
                new_values: { ...oldValuesSnapshot, ...updateData }
              });
            } catch {}
            // Ensure Work Calendar reflects the change immediately
            clearAllCaches();
            await fetchEvents();
            setIsModalOpen(false);
          }
        } catch (error) {
          setError('Failed to update existing event: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
        return;
      }
      // Otherwise, create new event
      const eventConfig = EVENT_CONFIGS[selectedEventType];
      if (!eventConfig) {
        setError('Invalid event type selected.');
        setIsSubmitting(false);
        setIsOperationInProgress(false);
        return;
      }
      const now = new Date();
      const offset = (eventDate && isBST(eventDate)) ? '+01:00' : '+00:00';
      
      // Prepare event data based on event type
      let eventDateTime: string | null = null;
      let title: string = eventConfig.name;
      // Get organization name from customer for audit trail
      const organizationName = customer?.company || customerData?.company || null;
      
      let eventData: any = {
        project_id: projectId,
        event_type: selectedEventType,
        email_template_used: selectedEventType,
        created_at: now.toISOString(),
        updated_at: now.toISOString(),
        title,
        status: 'scheduled' as const,
        created_by: user?.id || null,
        updated_by: user?.id || null,
        metadata: organizationName ? { organization_name: organizationName } : {}
      };

      // Handle date/time and duration based on event type
      if (eventConfig && eventConfig.requiresCalendarEvent) {
        // For calendar events, use user-specified date/time
        eventDateTime = (eventDate && eventTime) ? `${eventDate}T${eventTime}:00${offset}` : null;
        eventData.event_date = eventDateTime;
        if (isMultiDay && endDate) {
          eventData.end_date = `${endDate}T${eventTime || '09:00'}:00${isBST(endDate) ? '+01:00' : '+00:00'}`;
        }
      } else {
        // For non-calendar events, use current time
        eventDateTime = now.toISOString();
        eventData.event_date = eventDateTime;
      }


      
      // Verify project exists and user has access
      const projectExists = await verifyProject(projectId);
      if (!projectExists) {
        console.error('DEBUG: Project does not exist or user does not have access:', projectId);
        setError('Project not found or you do not have access to it');
        setIsSubmitting(false);
        return;
      }

      // Try to insert the event using direct Supabase
      try {
        const { data: insertData, error } = await directSupabase
          .from('project_events')
          .insert([eventData])
          .select()
          .single();
        
        if (error) {
          setError('Failed to create event: ' + error.message);
          return;
        }
        
        if (insertData && insertData.id) {
          await new Promise(res => setTimeout(res, 500));
          // Send email only after event is successfully created
          const { emailLogId: newEmailLogId, status: newStatus } = await sendTemplateEmail({
            templateType: selectedEventType,
            project,
            customer: convertToLegacyCustomer(customerData),
            variables: selectedEventType === 'installationDateConfirmation' ? {
              installationDate: eventDate,
              installationTime: eventTime,
              installationDateLong: eventDate && eventTime ? new Date(`${eventDate}T${eventTime}`).toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              }) : ''
            } : selectedEventType === 'deliveryDateConfirmation' ? {
              deliveryDate: eventDate,
              deliveryTime: eventTime,
              deliveryDateLong: eventDate && eventTime ? new Date(`${eventDate}T${eventTime}`).toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              }) : ''
            } : (selectedEventType === 'depositRequest' || selectedEventType === 'outstandingPayment') && depositInvoice ? (() => {
              const total = Number((depositInvoice as any).grand_total ?? 0) || 0;
              const paidDirect = Number((depositInvoice as any).amount_paid ?? 0) || 0;
              const paidFromHistory = Array.isArray((depositInvoice as any).payment_history)
                ? (depositInvoice as any).payment_history.reduce((sum: number, p: any) => sum + (Number(p.payment_amount) || 0), 0)
                : 0;
              const paid = Math.max(paidDirect, paidFromHistory);
              const outstanding = (depositInvoice as any).amount_outstanding != null
                ? Number((depositInvoice as any).amount_outstanding)
                : Math.max(total - paid, 0);
              const rawPct = Number((depositInvoice as any).deposit_percentage);
              const pct = Number.isFinite(rawPct) ? rawPct : null;
              let deposit = Number((depositInvoice as any).deposit_amount ?? 0) || 0;
              
              // Only calculate deposit if the invoice actually has a deposit configured
              const hasDeposit = (depositInvoice as any).has_deposit;
              if (hasDeposit) {
                if (!deposit) {
                  const listDeposit = depositInvoice.deposits?.[0]?.deposit_amount;
                  if (typeof listDeposit === 'number') deposit = listDeposit;
                }
                if (!deposit && pct != null) {
                  deposit = Number(((total * pct) / 100).toFixed(2));
                }
              } else {
                // If invoice has no deposit configured, ensure deposit is 0
                deposit = 0;
              }
              const paymentLink = (depositInvoice as any).payment_link || (depositInvoice as any).payment_url || '';
              const invoiceAddress = (depositInvoice as any).customer_address || (depositInvoice as any).billing_address || (depositInvoice as any).customer_billing_address || ((depositInvoice as any).customer_info?.[0]?.address) || '';
              // Compute outstanding explicitly: total minus deposit minus any additional payments beyond the deposit
              const nonDepositPaid = Math.max(paid - deposit, 0);
              const balance = Math.max(total - deposit - nonDepositPaid, 0);
              const resolvedDepositPct =
                (hasDeposit && pct != null)
                  ? pct
                  : (hasDeposit && total > 0 && deposit > 0)
                    ? (deposit / total) * 100
                    : null;
            return {
                invoiceRef: depositInvoice.document_ref || String(depositInvoice.id || ''),
                invoiceStatus: (depositInvoice as any).payment_status || (depositInvoice as any).status || '',
                depositAmount: deposit.toFixed(2),
                depositPercentage: resolvedDepositPct != null ? String(Math.round(resolvedDepositPct)) : '',
                amountOutstanding: balance.toFixed(2),
                paymentDueDate: (depositInvoice as any).payment_due_date || (depositInvoice as any).due_date || '',
                paymentLink,
                customerAddress: customerData.address || invoiceAddress,
              depositPresent: deposit > 0 ? 'true' : '',
              projectName: (depositInvoice as any).title || project.project_name || '',
              documentId: String((depositInvoice as any).id || ''),
              sentVersionNumber: (depositInvoice as any).sentVersionNumber != null ? String((depositInvoice as any).sentVersionNumber) : '',
              };
            })() : {
              surveyDate: eventDate,
              surveyTime: eventTime,
              surveyDateLong: eventDate && eventTime ? new Date(`${eventDate}T${eventTime}`).toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              }) : ''
            }
          });

          // Restore calendar event creation logic
          let calendarEventId = null;
          if (eventConfig && eventConfig.requiresCalendarEvent && eventDate && eventTime) {
            try {
              const organizationName = customerData?.company || null;
              const { data: templateData } = await emailTemplates.getByType(selectedEventType, organizationName);
              const calendarSettings = (templateData as any)?.calendar_event_settings;
              const templateVariables = buildEventTemplateVariables(selectedEventType, eventDate, eventTime, project, customerData);
              const projectForEvent = {
                ...project,
                customer_address: customerData.address || '',
                customer_phone: customerData.phone || '',
                customer_notes: project.notes || '',
              };
              calendarEventId = await createSurveyCalendarEventWithId({
                project: projectForEvent,
                date: eventDate,
                time: eventTime,
                calendarEventSettings: {
                  ...(calendarSettings && typeof calendarSettings === 'object' ? calendarSettings : {}),
                  duration_minutes: eventDuration
                },
                variables: templateVariables
              });
              // If multi-day, extend the Outlook event end to endDate at same time
              if (isMultiDay && endDate && calendarEventId) {
                const client = await getGraphClient();
                const updateOffset = isBST(endDate) ? '+01:00' : '+00:00';
                const newEnd = `${endDate}T${eventTime || '09:00'}:00${updateOffset}`;
                await client.api(`/me/events/${calendarEventId}`).patch({
                  end: { dateTime: newEnd, timeZone: 'Europe/London' }
                });
              }
            } catch (err) {
              // Don't throw here - we still want to update the event with email details
            }
          }

          await directSupabase
            .from('project_events')
            .update({
              email_sent_id: newEmailLogId,
              email_status: newStatus,
              email_sent_at: new Date().toISOString(),
              calendar_event_id: calendarEventId,
              related_invoice_link_id: null,
              updated_at: new Date().toISOString()
            })
            .eq('id', insertData.id);
          
          // Ensure Work Calendar reflects the new event immediately
          clearAllCaches();
          await fetchEvents();
          setIsModalOpen(false);
        } else {
          setError('Failed to create event: No event ID returned');
          return;
        }
      } catch (error) {
        console.error('DEBUG: Event creation failed:', error);
        setError('Failed to create event: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to handle event submission');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        setIsOperationInProgress(false);
      }, 100);
    }
  };

  // Helper to robustly parse event dates as UTC if needed
  function parseEventDate(dateStr: string) {
    if (dateStr && !dateStr.match(/(Z|[+-][0-9]{2}:?[0-9]{2})$/)) {
      return new Date(dateStr + 'Z');
    }
    return new Date(dateStr);
  }

  // Helper to render each event card
  const renderEventCard = (event: ProjectEvent) => {
    
    const config = EVENT_CONFIGS[event.event_type] || { name: event.event_type, requiresCalendarEvent: false };
    const eventDate = event.event_date ? parseEventDate(event.event_date) : null;
    const createdDate = event.created_at ? new Date(event.created_at) : null;
    // Debug log: log event.email_sent_id (or a note if missing)
    const logs = auditLogs[event.id] || [];
    return (
      <Card key={event.id} variant="outlined">
        <CardContent>
          <Box sx={{ flex: 1 }}>
            <Typography level="title-md" sx={{ mb: 1 }}>{config.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Tooltip title={event.email_sent_id ? 'View the email activity for this event' : 'No email activity available for this event'}>
                <span>
                  <Button
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => event.email_sent_id && handleViewEmail(event.email_sent_id)}
                    startDecorator={<IconWrapper icon={FaEnvelope} />}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                    disabled={!event.email_sent_id}
                  >
                    View Email
                  </Button>
                </span>
              </Tooltip>
              {event.calendar_event_id && (
                <Chip
                  size="sm"
                  variant="soft"
                  color="primary"
                  component="a"
                  href={outlookCalendarItemUrl(event.calendar_event_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  startDecorator={<IconWrapper icon={FaCalendarAlt} />}
                  sx={{ ml: 0 }}
                >
                  View Calendar Event
                </Chip>
              )}
            </Box>
            {config.requiresCalendarEvent && eventDate && (
              <Typography level="body-sm" color="neutral">
                <strong>Event Date:</strong> {eventDate.toLocaleString('en-GB', { timeZone: 'Europe/London' })}
              </Typography>
            )}
            <Typography level="body-sm" color="neutral">
              <strong>Created:</strong> {event.created_at ? new Date(event.created_at).toLocaleString() : 'Unknown'}
            </Typography>
            {event.email_sent_at && (
              <Typography level="body-sm" color="neutral">
                <strong>Email Sent:</strong> {new Date(event.email_sent_at).toLocaleString('en-GB', { timeZone: 'Europe/London' })}
              </Typography>
            )}
            {event.calendar_event_id && (
              <Typography level="body-sm" color="neutral" sx={{ wordBreak: 'break-all', overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>
                <strong>Calendar Event ID:</strong> {event.calendar_event_id}
              </Typography>
            )}
          </Box>
          {/* Audit log accordion */}
          {logs.length > 0 ? (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary>Audit Trail</AccordionSummary>
              <AccordionDetails>
                {logs.map((log, idx) => (
                  <Box key={log.id} sx={{ mb: 1 }}>
                    <Typography level="body-xs">
                      <strong>{log.change_type}</strong>
                      {idx === 0 && (
                        <span style={{ color: '#1976d2', fontWeight: 500, marginLeft: 6 }}>(current)</span>
                      )}
                      {' '}by {log.changed_by || 'unknown'} on {log.changed_at ? new Date(log.changed_at).toLocaleString('en-GB', { timeZone: 'Europe/London' }) : 'unknown'}
                    </Typography>
                    {log.change_type === 'UPDATE' && log.old_values && log.new_values && (
                      <Typography level="body-xs" sx={{ ml: 2 }}>
                        Changed fields: {Object.keys(log.new_values).filter(k => log.old_values[k] !== log.new_values[k]).join(', ')}
                      </Typography>
                    )}
                    {/* Add View Email chip if email_sent_id is present in new_values or old_values */}
                    {((log.new_values && log.new_values.email_sent_id) || (log.old_values && log.old_values.email_sent_id)) && (
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => handleViewEmail(log.new_values?.email_sent_id || log.old_values?.email_sent_id)}
                        startDecorator={<IconWrapper icon={FaEnvelope} />}
                        sx={{ minWidth: 'auto', p: 0.5, ml: 1 }}
                        disabled={!(log.new_values?.email_sent_id || log.old_values?.email_sent_id)}
                      >
                        View Email
                      </Button>
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ) : (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary>Audit Trail</AccordionSummary>
              <AccordionDetails>
                <Typography level="body-xs">No audit log entries found for this event.</Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
    );
  };

  // Add a helper function to determine if date/time fields should be shown
  const shouldShowDateTimeFields = (eventType: EventType | null) => {
    if (!eventType) return false;
    const eventConfig = EVENT_CONFIGS[eventType];
    return eventConfig?.requiresCalendarEvent || false;
  };

  // Update the modal content to use the helper function
  const renderEventModal = () => {
    const showDateTimeFields = shouldShowDateTimeFields(selectedEventType);
    const isCustomerMissing = !customer?.id;
    const customerName = customer?.name || 'Unknown Customer';
    const customerEmail = customer?.email || '';
    const customerAddress = customer?.address || '';

    return (
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalDialog>
          <DialogTitle>
            {duplicateEvent ? 'Update Event' : 'Create New Event'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Event Type</FormLabel>
                <Select
                  value={selectedEventType || ''}
                  onChange={(_, value) => setSelectedEventType(value as EventType)}
                >
                  {Object.entries(EVENT_CONFIGS)
                    .filter(([type]) => {
                      // Filter by project properties
                      if (type === 'installationDateConfirmation' && project?.supply_only) return false;
                      if (type === 'deliveryDateConfirmation' && !project?.supply_only) return false;
                      
                      // Filter by organization - check both configuration AND if template exists in database
                      const organizationName = customer?.company || null;
                      
                      // First check if event type is allowed for this organization (from configuration)
                      if (organizationName && !isEventTypeAllowed(type, organizationName)) {
                        return false;
                      }
                      
                      // Then check if a template actually exists in the database for this organization
                      if (organizationName && !availableTemplates.has(type)) {
                        return false;
                      }
                      
                      return true;
                    })
                    .map(([type, config]) => (
                      <Option key={type} value={type}>
                        {config.name}
                      </Option>
                    ))}
                </Select>
              </FormControl>

              {(selectedEventType === 'depositRequest' || selectedEventType === 'outstandingPayment') && (
                <FormControl>
                  <FormLabel>Invoice</FormLabel>
                  <Select
                    value={depositInvoice?.id || ''}
                    onChange={(_, value) => {
                      // Find JARVIS document
                      const jarvisDoc = jarvisDocuments.find(d => d.id === value);
                      if (jarvisDoc) {
                        // Calculate outstanding amount using the service
                        const outstandingAmount = documentsIntegrationService.calculateOutstandingBalance(jarvisDoc);
                        
                        // Create a mock invoice structure for JARVIS documents
                        const mockInvoice = {
                          id: jarvisDoc.id,
                          document_ref: jarvisDoc.invoiceRef || jarvisDoc.lifecycleRef,
                          title: jarvisDoc.title,
                          grand_total: jarvisDoc.grandTotal,
                          deposit_amount: jarvisDoc.depositAmount,
                          deposit_percentage: jarvisDoc.depositPercentage ? jarvisDoc.depositPercentage * 100 : null,
                          has_deposit: jarvisDoc.hasDeposit,
                          amount_paid: 0,
                          amount_outstanding: outstandingAmount,
                          payment_status: 'unpaid',
                          customer_address: customer?.address || '',
                          payment_link: '',
                          payment_url: '',
                          sentVersionNumber: jarvisDoc.versionNumber ?? null,
                          deposits: jarvisDoc.hasDeposit ? [{
                            id: 1,
                            quote_id: parseInt(jarvisDoc.id),
                            deposit_amount: jarvisDoc.depositAmount,
                            deposit_percentage: jarvisDoc.depositPercentage ? jarvisDoc.depositPercentage * 100 : 0,
                            description: 'JARVIS Document Deposit'
                          }] : []
                        };
                        setDepositInvoice(mockInvoice as any);
                      }
                    }}
                  >
                    {/* JARVIS Documents - filter based on event type */}
                    {jarvisDocuments
                      .filter((doc) => selectedEventType === 'depositRequest'
                        ? normalizeInvoiceRole(doc.invoiceRole) === 'deposit' || (doc.kind === 'Invoice' && doc.hasDeposit)
                        : ['works', 'variation'].includes(String(doc.invoiceRole || '')) || (doc.kind === 'Invoice' && !doc.invoiceRole))
                      .map(doc => (
                        <Option key={doc.id} value={doc.id}>
                          {doc.invoiceRef || doc.lifecycleRef} - {doc.title}
                        </Option>
                      ))}
                  </Select>
                </FormControl>
              )}

              {showDateTimeFields && (
                <>
                  <FormControl>
                    <FormLabel>Date</FormLabel>
                    <Input
                      type="date"
                      value={eventDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventDate(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Time</FormLabel>
                    <TimePicker15Min
                      value={eventTime}
                      onChange={setEventTime}
                      placeholder="Select time"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Duration (minutes)</FormLabel>
                    <Input
                      type="number"
                      value={eventDuration}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventDuration(parseInt(e.target.value))}
                      disabled={isMultiDay}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Multi-day event?</FormLabel>
                    <Select value={isMultiDay ? 'yes' : 'no'} onChange={(_, v) => setIsMultiDay(v === 'yes')}>
                      <Option value="no">No</Option>
                      <Option value="yes">Yes</Option>
                    </Select>
                  </FormControl>
                  {isMultiDay && (
                    <FormControl>
                      <FormLabel>End Date</FormLabel>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                      />
                    </FormControl>
                  )}
                </>
              )}

              {isCustomerMissing && (
                <Alert color="warning" variant="soft">
                  Customer information is missing. Please add customer details before creating events.
                </Alert>
              )}

              {duplicateEvent && selectedEventType && (
                <Alert color="warning" variant="soft">
                  An event of this type already exists for this project{duplicateEvent.email_sent_id ? ' and an email has already been sent' : ''}. Sending again will update the existing record and send another email.
                </Alert>
              )}

              {(selectedEventType === 'depositRequest' || selectedEventType === 'outstandingPayment') && (
                <>
                  {depositBlockingError ? (
                    <Alert color="danger" variant="soft">
                      {depositBlockingError}
                    </Alert>
                  ) : depositWarnings.length > 0 ? (
                    <Alert color="warning" variant="soft">
                      {depositWarnings.map((w, i) => (
                        <div key={i}>{w}</div>
                      ))}
                    </Alert>
                  ) : null}
                </>
              )}

              {error && (
                <Alert color="danger" variant="soft">
                  {error}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEventSubmit}
              loading={isSubmitting}
              disabled={!!(
                isCustomerMissing ||
                !selectedEventType ||
                  (showDateTimeFields && (!eventDate || !eventTime)) ||
                  ((selectedEventType === 'depositRequest' || selectedEventType === 'outstandingPayment') && !!depositBlockingError)
              )}
            >
              {duplicateEvent ? 'Update Event' : 'Create Event'}
            </Button>
          </DialogActions>
          {/* Live Email Preview */}
          <Divider sx={{ my: 2 }} />
          <Typography level="title-lg">Email Preview</Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>Subject: {previewSubject || '(no subject)'}</Typography>
          <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'sm', backgroundColor: 'background.level1', maxHeight: 300, overflow: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: previewHtml || '<div style="color:#888">No preview available</div>' }} />
          </Sheet>
        </ModalDialog>
      </Modal>
    );
  };

  // Add back the handleViewEmail function
  const handleViewEmail = async (emailId: string) => {
    console.log("handleViewEmail called with emailId:", emailId);
    setEmailModalLoading(true);
    setEmailModalError(null);
    setShowEmailModal(true);
    try {
      const { data, error } = await emailActivity.getById(emailId);
      if (error) {
        console.error("handleViewEmail fetch error:", error);
        throw new Error(error);
      }
      console.log("handleViewEmail fetch result:", data);
      setEmailLog(data);
    } catch (err) {
      console.error("handleViewEmail caught error:", err);
      setEmailModalError(err instanceof Error ? err.message : 'Failed to load email activity');
      setEmailLog(null);
    } finally {
      setEmailModalLoading(false);
    }
  };

  const eventModals = (
    <>
      {renderEventModal()}
      <Modal open={showEmailModal} onClose={() => setShowEmailModal(false)}>
        <ModalDialog sx={{ maxWidth: { xs: '98vw', sm: 800 }, width: '100%', maxHeight: '90vh', overflow: 'auto', p: { xs: 1, sm: 3 } }}>
          {emailModalLoading ? (
            <Stack alignItems="center" justifyContent="center" minHeight={200}>
              <Typography>Loading...</Typography>
            </Stack>
          ) : emailModalError ? (
            <Stack alignItems="center" justifyContent="center" minHeight={200}>
              <Typography color="danger">{emailModalError}</Typography>
            </Stack>
          ) : emailLog ? (
            <Stack spacing={2}>
              <Typography level="title-lg" sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>Email Preview</Typography>
              <Typography level="body-sm" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>Subject: {emailLog.subject}</Typography>
              <Typography level="body-sm" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>Recipient: {emailLog.recipient}</Typography>
              <Typography level="body-sm" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>Status: {emailLog.status}</Typography>
              {emailLog.error_message && (
                <Typography color="danger">Error: {emailLog.error_message}</Typography>
              )}
              <Divider />
              <Sheet variant="outlined" sx={{ mt: 1, p: { xs: 1, sm: 2 }, borderRadius: 'sm', backgroundColor: 'background.level1', width: '100%', overflowX: 'auto' }}>
                <div style={{ minWidth: 0, wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: emailLog.content_html }} />
              </Sheet>
            </Stack>
          ) : null}
        </ModalDialog>
      </Modal>
    </>
  );

  if (modalOnly) {
    return eventModals;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100vw', p: compact ? 0 : { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 2, gap: { xs: 1, sm: 2 } }}>
        <Box>
          <Typography level={compact ? 'title-md' : 'title-lg'} sx={compact ? undefined : { fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
            Project events
          </Typography>
          {compact && (
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              Create first survey, final survey, and installation here. Dated bookings show in Key dates.
            </Typography>
          )}
        </Box>
        <Button
          onClick={() => {
            console.log('Create Event button clicked');
            console.log('Current project:', project);
            console.log('Current customer:', customer);
            setIsModalOpen(true);
          }}
          startDecorator={<IconWrapper icon={FaPlus} />}
          sx={{ width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.95rem', sm: '1rem' }, py: { xs: 1, sm: compact ? 1 : 1.5 } }}
        >
          Create Event
        </Button>
      </Box>
      {(() => {
        const listedEvents = compact
          ? events.filter((event) => !CALENDAR_EVENT_TYPES.has(event.event_type))
          : events;
        if (listedEvents.length === 0) {
          return compact ? (
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              {events.length === 0
                ? 'No events recorded for this project yet.'
                : 'Dated survey and install bookings are in Key dates.'}
            </Typography>
          ) : (
            <Card variant="outlined" sx={{ width: '100%', maxWidth: '100vw', p: { xs: 1, sm: 2 } }}>
              <CardContent>
                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>No events recorded for this project yet.</Typography>
              </CardContent>
            </Card>
          );
        }
        return (
          <Stack spacing={2} sx={{ width: '100%' }}>
            {listedEvents.map(renderEventCard)}
          </Stack>
        );
      })()}
      {eventModals}
    </Box>
  );
};

export default EventManager;